import { z } from 'zod';
import { financeMoneyCentsSchema, financeQuantityTenThousandthsSchema, financeTaxBasisPointsSchema, } from './finance-invoice.contract.js';
export const financeEstimatingBillableRevisionsCapability = 'estimating.billable-revisions.v1';
export const financeEstimatingBillableRevisionSlot = 'finance.invoice-billable-revision';
export const financeEstimatingBillableRevisionProviderId = 'estimating.server.finance-billable-revision';
const nullableText = (maximum) => z.string().trim().min(1).max(maximum).nullable();
export const financeEstimatingBillableRevisionRequestSchema = z.strictObject({
    consumerModuleId: z.literal('finance'),
    requestedCapability: z.literal(financeEstimatingBillableRevisionsCapability),
    resourceType: z.literal('finance.invoice'),
    resourceId: z.string().uuid(),
    preferredSourceId: z.string().uuid().nullable(),
    projectId: z.string().uuid(),
});
export const financeEstimatingBillableLineSchema = z.strictObject({
    lineKey: z.string().uuid(),
    lineNumber: z.number().int().positive(),
    sourceCapability: nullableText(160),
    sourceId: nullableText(240),
    sourceLabel: nullableText(240),
    groupLabel: nullableText(120),
    name: z.string().trim().min(1).max(240),
    description: nullableText(4_000),
    quantityTenThousandths: financeQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: financeMoneyCentsSchema,
    taxable: z.boolean(),
    lineTotalCents: financeMoneyCentsSchema,
});
export const financeEstimatingBillableRevisionFactSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(financeEstimatingBillableRevisionsCapability),
    estimateId: z.string().uuid(),
    estimateVersionId: z.string().uuid(),
    estimateRevision: z.number().int().positive(),
    estimateStatus: z.enum(['draft', 'released', 'viewed', 'accepted', 'declined', 'expired']),
    estimateContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    projectId: z.string().uuid(),
    projectRevision: z.number().int().positive(),
    title: z.string().trim().min(1).max(240),
    currencyCode: z.literal('USD'),
    taxBasisPoints: financeTaxBasisPointsSchema,
    totals: z.strictObject({
        subtotalCents: financeMoneyCentsSchema,
        discountCents: financeMoneyCentsSchema,
        taxCents: financeMoneyCentsSchema,
        totalCents: financeMoneyCentsSchema,
    }),
    lines: z.array(financeEstimatingBillableLineSchema).min(1).max(500),
}).superRefine((fact, context) => {
    const subtotal = fact.lines.reduce((sum, line) => sum + BigInt(line.lineTotalCents), 0n);
    if (subtotal !== BigInt(fact.totals.subtotalCents)) {
        context.addIssue({
            code: 'custom', path: ['totals', 'subtotalCents'],
            message: 'The Estimating billable line subtotal is invalid.',
        });
    }
    if (fact.totals.totalCents !==
        fact.totals.subtotalCents - fact.totals.discountCents + fact.totals.taxCents) {
        context.addIssue({
            code: 'custom', path: ['totals', 'totalCents'],
            message: 'The Estimating billable total is invalid.',
        });
    }
});
export const financeEstimatingBillableRevisionUnavailableReasonSchema = z.enum([
    'not-installed',
    'not-enabled',
    'permission-denied',
    'no-current-estimate',
    'preferred-estimate-not-found',
    'estimate-not-billable',
    'no-billable-lines',
    'provider-failed',
    'provider-invalid',
]);
export const financeEstimatingBillableRevisionAvailabilitySchema = z.discriminatedUnion('state', [
    financeEstimatingBillableRevisionFactSchema,
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.literal(financeEstimatingBillableRevisionsCapability),
        reasonCode: financeEstimatingBillableRevisionUnavailableReasonSchema,
    }),
]);
//# sourceMappingURL=finance-estimating-billable-revision.contract.js.map